import styled, { css, keyframes } from "styled-components";

type ButtonProps = {
  appearance?: "primary" | "secondary";
  variant?: "default" | "outline";
  size?: "default" | "small" | "large";
  isFancy?: boolean;
};

const defaultCSS = css<ButtonProps>`
  background: ${(props) =>
    props.appearance === "secondary"
      ? "linear-gradient(135deg, rgba(78, 56, 178, 0.9), rgba(52, 38, 120, 0.9))"
      : "linear-gradient(135deg, rgba(89, 44, 166, 0.9), rgba(66, 33, 123, 0.9))"};
  backdrop-filter: blur(10px);
`;

const outlineCSS = css<ButtonProps>`
  background: transparent;
  border: 2px solid rgba(255, 255, 255, 0.1) !important;
  color: rgba(255, 255, 255, 0.8);
`;

export const Button = styled.button<ButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  text-align: center;
  font-family: var(--altFontFamily);
  min-width: ${(props) => (props.size === "small" ? "120px" : "180px")};
  font-size: ${(props) => (props.size === "small" ? "14px" : "16px")};
  font-weight: 600;
  letter-spacing: 0.5px;
  touch-action: manipulation;
  padding: ${(props) => (props.size === "small" ? "8px 16px" : "12px 24px")};
  color: rgba(255, 255, 255, 0.95);
  border: none;
  ${(props) => (props.variant === "outline" ? outlineCSS : defaultCSS)}
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15),
    inset 0 1px 1px rgba(255, 255, 255, 0.1);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 25px rgba(89, 44, 166, 0.25),
      inset 0 1px 1px rgba(255, 255, 255, 0.2);
  }

  &:active {
    transform: translateY(1px);
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1),
      inset 0 1px 1px rgba(255, 255, 255, 0.1);
  }

  &[disabled] {
    opacity: 0.5;
    pointer-events: none;
    cursor: not-allowed;
  }

  ${(props) =>
    props.isFancy &&
    css`
      background: linear-gradient(
        90deg,
        rgba(89, 44, 166, 0.9) 0%,
        rgba(123, 44, 191, 0.9) 50%,
        rgba(89, 44, 166, 0.9) 100%
      );
      background-size: 200% auto;
      animation: ${fancyKeyframes} 4s linear infinite;
      box-shadow: 0 4px 20px rgba(89, 44, 166, 0.3),
        inset 0 1px 1px rgba(255, 255, 255, 0.2);
    `}

  @media (max-width: 600px) {
    min-width: ${(props) => (props.size === "small" ? "100px" : "150px")};
    font-size: ${(props) => (props.size === "small" ? "13px" : "15px")};
    padding: ${(props) => (props.size === "small" ? "6px 12px" : "10px 20px")};
  }
`;

const fancyKeyframes = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;
